// app/composables/useProgramData.ts
// Fetch et parsing des données de la page programme

import type { ComputedRef, MaybeRefOrGetter } from 'vue'
import { computed, ref, toValue } from 'vue'
import { getAxiosResponseStatus } from '~/utils/axios'
import type { CampusBlock, ProgramData, ProgramDataResponse, StatsBlock } from '~/types/school'
import {
  EMPTY_CAMPUS_BLOCK,
  EMPTY_PROGRAM_CONTENT_BLOCK,
  EMPTY_PROGRAM_DESCRIPTION_BLOCK,
  EMPTY_SEO_BLOCK,
  EMPTY_STATS_BLOCK,
} from '~/constants/empty'
import { parseBlock } from '~/utils/helpers'

interface UseProgramDataOptions {
  language: MaybeRefOrGetter<string>
  alias: MaybeRefOrGetter<string>
}

/**
 * Charge la fiche programme via `/api/aca/programdata` (SSR via `useAsyncData`).
 * @param {UseProgramDataOptions} options Langue + alias route (`école/programmes/slug-id`).
 * @returns {Promise<{ programData: Ref<ProgramDataResponse | null>; loading: ComputedRef<boolean>; error: ComputedRef<string | null>; lastHttpStatus: ComputedRef<number | undefined>; refresh: () => Promise<void> }>} Données et état de chargement.
 */
export const useProgramData = async (options: UseProgramDataOptions) => {
  const api = useFrontApi()
  const lastHttpStatus = ref<number | undefined>(undefined)

  const {
    data: programData,
    error,
    status,
    refresh,
  } = await useAsyncData<ProgramDataResponse | null>(
    () => `program-data-${toValue(options.language)}-${toValue(options.alias)}`,
    async () => {
      const language = toValue(options.language)
      const alias = toValue(options.alias)

      if (!alias || !language) {
        lastHttpStatus.value = undefined
        return null
      }

      const segments = alias.split('/')
      if (segments.length > 3) {
        console.warn(`Alias inattendu : ${alias}. Fetch annulé.`)
        return null
      }
      if (segments.length !== 3) return null

      const schoolAlias = segments[0]
      if (!schoolAlias) {
        console.warn(`Alias de l'école manquant dans ${alias}. Fetch annulé.`)
        return null
      }

      const prgElements = segments[2]
      if (!prgElements) {
        console.warn(`Elts de programme manquants dans ${alias}. Fetch annulé.`)
        return null
      }

      const prgid = prgElements.split('-').pop()
      if (!prgid) {
        console.warn(`ID de programme manquant dans ${alias}. Fetch annulé.`)
        return null
      }

      try {
        const endpoint: string = `/api/aca/programdata?language=${language}&alias=${schoolAlias}&prgid=${prgid}`
        const { data } = await api.get<ProgramData>(endpoint)

        lastHttpStatus.value = 200

        return {
          programdatas: data,
          parsedSchoolLinkBlock: parseBlock(data.schoolLinkBlock, EMPTY_SEO_BLOCK),
          parsedSchoolTypeBlock: parseBlock(data.schoolTypeBlock, EMPTY_SEO_BLOCK),
          parsedParentBlock: parseBlock(data.parentBlock, EMPTY_SEO_BLOCK),
          parsedCountriesBlock: parseBlock(data.countriesBlock, EMPTY_SEO_BLOCK),
          parsedCitiesBlock: parseBlock(data.citiesBlock, EMPTY_SEO_BLOCK),
          parsedProgramDescriptionBlock: parseBlock(
            data.programDescriptionBlock,
            EMPTY_PROGRAM_DESCRIPTION_BLOCK,
          ),
          parsedProgramDescrWhatBlock: parseBlock(
            data.programDescrWhatBlock,
            EMPTY_PROGRAM_CONTENT_BLOCK,
          ),
          parsedProgramDescrWhyBlock: parseBlock(
            data.programDescrWhyBlock,
            EMPTY_PROGRAM_CONTENT_BLOCK,
          ),
          parsedProgramAdmissionBlock: parseBlock(
            data.programAdmissionBlock,
            EMPTY_PROGRAM_CONTENT_BLOCK,
          ),
          parsedStatsBlock: parseBlock<StatsBlock>(data.statsBlock, EMPTY_STATS_BLOCK),
          parsedAddressesBlock: parseBlock<CampusBlock>(data.addressesBlock, EMPTY_CAMPUS_BLOCK),
        }
      } catch (e) {
        lastHttpStatus.value = getAxiosResponseStatus(e)
        return null
      }
    },
    {
      watch: [() => toValue(options.language), () => toValue(options.alias)],
    },
  )

  const loading: ComputedRef<boolean> = computed<boolean>(() => status.value === 'pending')

  return {
    programData,
    loading,
    error: computed(() => (error.value ? (error.value.message ?? 'Erreur inconnue') : null)),
    lastHttpStatus: computed(() => lastHttpStatus.value),
    refresh,
  }
}
